import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';
import useDebounce from 'hooks/useDebounce';
import { getTransactions } from '@redux/features/transactionsSlice';
import { getUsers } from '@redux/features/usersSlice';
import { getUkrainianPlural } from 'helpers/getUkrainianPlural';
import { scrollDate } from 'helpers/scrollDate';
import { formatDate } from 'helpers/formatDate';
import { TRANSACTION_TYPES } from 'constants/transactionTypes';
import { DatePicker } from 'components/DatePicker';
import { Checkbox } from 'components/Checkbox';
import { Dropdown } from 'components/Dropdown';
import { Input } from 'components/Input';
import styles from './CashboxPage.scss';

const TYPE_OPTIONS = [
  { label: 'Надходження', value: TRANSACTION_TYPES.INCOME },
  { label: 'Списання', value: TRANSACTION_TYPES.EXPENSE },
  { label: 'Вхідний переказ', value: TRANSACTION_TYPES.TRANSFER_IN },
  { label: 'Вихідний переказ', value: TRANSACTION_TYPES.TRANSFER_OUT },
];

export const Filters = ({ cashbox, hasActiveFilters, setHasActiveFilters, transactionsLength = 0 }) => {
  const { locations } = useSelector((state) => state.locations);
  const { users } = useSelector((state) => state.users);
  const [currentDate, setCurrentDate] = useState(formatDate(new Date()));
  const [enableDateFilter, setEnableDateFilter] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const dispatch = useDispatch();

  const debouncedSearchQuery = useDebounce(searchQuery);

  useEffect(() => {
    setHasActiveFilters(
      selectedType?.value !== undefined ||
        selectedLocation?.value !== undefined ||
        selectedOperator?.value !== undefined ||
        searchQuery.trim() !== '' ||
        enableDateFilter
    );
  }, [enableDateFilter, currentDate, selectedType, selectedLocation, selectedOperator, searchQuery]);

  const transactionParams = useMemo(() => {
    const params = {
      cashboxID: cashbox?.id,
      sortOrder: 'desc',
      page: 1,
    };

    if (enableDateFilter) {
      params.date = currentDate;
    }

    if (selectedType?.value) {
      params.type = selectedType.value;
    }

    if (selectedLocation?.value) {
      params.locationID = selectedLocation.value;
    }

    if (selectedOperator?.value) {
      params.operatorID = selectedOperator.value;
    }

    if (debouncedSearchQuery.trim()) {
      params.search = debouncedSearchQuery.trim();
    }

    return params;
  }, [cashbox, enableDateFilter, currentDate, selectedType, selectedLocation, selectedOperator, debouncedSearchQuery]);

  useEffect(() => {
    if (cashbox) dispatch(getTransactions(transactionParams));
  }, [dispatch, cashbox, transactionParams]);

  useEffect(() => {
    if (users.length === 0) {
      dispatch(getUsers());
    }
  }, [dispatch]);

  const handleDateChange = useCallback(
    (direction) => setCurrentDate((prev) => scrollDate(prev, direction)),
    [setCurrentDate]
  );

  const clearFilters = () => {
    setSelectedType(null);
    setSelectedLocation(null);
    setSelectedOperator(null);
    setSearchQuery('');
    setEnableDateFilter(false);
  };

  return (
    <div className={styles.filtersSection}>
      <div className={styles.filtersHeader}>
        <h3 className={styles.sectionTitle}>Фільтри</h3>
        {hasActiveFilters && (
          <button className={styles.clearButton} onClick={clearFilters}>
            <FontAwesomeIcon icon={faTimes} />
            Очистити
          </button>
        )}
      </div>

      <div className={styles.filterGroup}>
        <label>Пошук</label>
        <Input
          className={styles.input}
          icon={faSearch}
          type='text'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder='Пошук по опису та сумі'
        />
      </div>

      <div className={styles.filterGroup}>
        <label>Дата</label>
        <Checkbox
          label='Фільтрувати по даті'
          checked={enableDateFilter}
          onChange={() => setEnableDateFilter(!enableDateFilter)}
        />
        {enableDateFilter && (
          <div className={styles.dateControl}>
            <DatePicker
              date={currentDate}
              handleScrollDate={handleDateChange}
              enableCalendar
              disabled={!enableDateFilter}
            />
          </div>
        )}
      </div>

      <div className={styles.filterGroup}>
        <label>Тип транзакції</label>
        <Dropdown value={selectedType} options={TYPE_OPTIONS} onChange={setSelectedType} placeholder='Оберіть тип' />
      </div>

      <div className={styles.filterGroup}>
        <label>Локація відправник</label>
        <Dropdown
          value={selectedLocation}
          options={locations.map((l) => ({ label: l.name, value: l.id }))}
          onChange={setSelectedLocation}
          placeholder='Оберіть локацію'
        />
      </div>

      <div className={styles.filterGroup}>
        <label>Оператор</label>
        <Dropdown
          value={selectedOperator}
          options={users.map((u) => ({ label: `${u.surname} ${u.name}`, value: u.id }))}
          onChange={setSelectedOperator}
          placeholder='Оберіть оператора'
        />
      </div>

      {hasActiveFilters && transactionsLength !== undefined && (
        <div className={styles.filtersStatus}>
          Знайдено {transactionsLength}{' '}
          {getUkrainianPlural(transactionsLength, ['транзакція', 'транзакції', 'транзакцій'])}
        </div>
      )}
    </div>
  );
};
